import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AssignmentCard } from "./AssignmentCard";
import { AssignmentResultDialog } from "./AssignmentResultDialog";
import { SendTestDialog } from "./SendTestDialog";
import { throwIfNotOk } from "@/lib/api-error";
import type { TestAssignment } from "@/types/test";

interface Props {
  therapyId: string;
}

export function TherapyTestsPanel({ therapyId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [sendOpen, setSendOpen] = useState(false);
  const [viewingId, setViewingId] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["therapist", "test-assignments", "by-therapy", therapyId],
    queryFn: async () => {
      const res = await fetch(`/api/test-assignments?therapyId=${therapyId}`);
      await throwIfNotOk(res, t("tests.therapist.fetchError"));
      return res.json() as Promise<{ assignments: TestAssignment[] }>;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/test-assignments/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, t("tests.therapist.deleteError"));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["therapist", "test-assignments", "by-therapy", therapyId] }),
  });

  return (
    <div className="mt-3 rounded-xl border border-border/70 bg-secondary/40 p-4">
      <div className="flex items-center justify-between gap-3 pb-3">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary/70">
          <span
            className="font-heading text-sm italic tabular-nums leading-none"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            §
          </span>
          <span>{t("tests.therapist.therapyPanel.title")}</span>
        </p>
        <Button type="button" size="xs" className="rounded-full" onClick={() => setSendOpen(true)}>
          <Plus />
          {t("tests.therapist.send")}
        </Button>
      </div>
      <div className="space-y-2">
        {data?.assignments.length === 0 && (
          <p className="px-1 py-1 text-xs italic text-muted-foreground">
            {t("tests.therapist.therapyPanel.empty")}
          </p>
        )}
        {data?.assignments.map((a) => (
          <AssignmentCard key={a.id} assignment={a} onView={setViewingId} onDelete={(id) => deleteMutation.mutate(id)} />
        ))}
      </div>
      <SendTestDialog
        open={sendOpen}
        defaultTherapyId={therapyId}
        onOpenChange={setSendOpen}
        onCreated={() =>
          queryClient.invalidateQueries({ queryKey: ["therapist", "test-assignments", "by-therapy", therapyId] })
        }
      />
      <AssignmentResultDialog assignmentId={viewingId} onClose={() => setViewingId(null)} />
    </div>
  );
}
